import { DOCUMENT } from '@angular/common'
import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core'
import { environment } from '@se-po/shared-environments'
import { SeFeTranslationsLoader } from 'se-fe-translations'

const ELIGIBILITY_ELEMENT = 'persona-eligibility-list'
const ELIGIBILITY_SCRIPT_ATTRIBUTE = 'data-se-persona-eligibility-script'
const ELIGIBILITY_STYLES_ATTRIBUTE = 'data-se-persona-eligibility-styles'
const TRANSLATIONS_PATH = 'assets/shared/ui-persona-eligibility/assets/translations'

@Component({
  selector: 'se-po-persona-eligibility',
  templateUrl: './persona-eligibility.component.html',
  styleUrls: ['./persona-eligibility.component.scss'],
  standalone: false,
})
export class PersonaEligibilityComponent implements OnInit, OnChanges, OnDestroy {
  private static scriptLoaded?: Promise<void>

  @ViewChild('contents', { static: true }) private contents!: ElementRef<HTMLElement>

  @Input() public personaId!: number
  @Input() public contextOrgId!: number
  @Input() public eligibilityRuleSetIds: string[] | null = null
  @Input() public affiliationOrganizationId?: number
  @Input() public refreshKey = 0
  @Output() public readonly membershipCanceled = new EventEmitter<{ membershipId: string }>()

  public readonly translateKey = 'SE_PO_SHARED_UI_PERSONA_ELIGIBILITY.'
  public loadFailed = false

  private readonly document = inject(DOCUMENT)
  private readonly renderer = inject(Renderer2)
  private readonly translationsLoader = inject(SeFeTranslationsLoader)
  private destroyed = false
  private personaEligibilityListProps = ''
  private removeMembershipCanceledListener?: () => void

  public ngOnInit(): void {
    this.setPersonaEligibilityListProps()
    this.includeStyles()
    void this.initialize()
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.refreshKey || changes.refreshKey.firstChange) return

    this.setPersonaEligibilityListProps()
    void this.initialize()
  }

  public ngOnDestroy(): void {
    this.destroyed = true
    this.removeMembershipCanceledListener?.()
  }

  private async initialize(): Promise<void> {
    this.loadFailed = false
    try {
      await Promise.all([
        this.translationsLoader.load(TRANSLATIONS_PATH),
        this.loadComponent(),
      ])
    } catch {
      if (!this.destroyed) this.loadFailed = true
    }
  }

  private setPersonaEligibilityListProps(): void {
    this.personaEligibilityListProps = JSON.stringify({
      persona_id: this.personaId,
      eligibility_rule_set_ids: this.eligibilityRuleSetIds,
      context_organization_id: this.contextOrgId,
      affiliation_organization_id: this.affiliationOrganizationId,
    })
  }

  private loadScript(): Promise<void> {
    if (this.document.defaultView?.customElements.get(ELIGIBILITY_ELEMENT)) return Promise.resolve()
    if (PersonaEligibilityComponent.scriptLoaded) return PersonaEligibilityComponent.scriptLoaded

    PersonaEligibilityComponent.scriptLoaded = new Promise<void>((resolve, reject) => {
      const existingScript = this.document.querySelector<HTMLScriptElement>(`script[${ELIGIBILITY_SCRIPT_ATTRIBUTE}]`)
      const script = existingScript || this.document.createElement('script')

      script.addEventListener('load', () => resolve(), { once: true })
      script.addEventListener('error', () => {
        script.remove()
        reject(new Error('Eligibility details could not be loaded'))
      }, { once: true })

      if (!existingScript) {
        script.async = true
        script.src = `${environment.eligibilityListUi}/eligibility-list-element.js`
        script.setAttribute(ELIGIBILITY_SCRIPT_ATTRIBUTE, '')
        this.document.body.appendChild(script)
      }
    }).catch(error => {
      PersonaEligibilityComponent.scriptLoaded = undefined
      throw error
    })

    return PersonaEligibilityComponent.scriptLoaded
  }

  private includeStyles(): void {
    if (this.document.querySelector(`link[${ELIGIBILITY_STYLES_ATTRIBUTE}]`)) return

    const stylesLink = this.document.createElement('link')
    stylesLink.rel = 'stylesheet'
    stylesLink.href = `${environment.eligibilityListUi}/styles.css`
    stylesLink.setAttribute(ELIGIBILITY_STYLES_ATTRIBUTE, '')
    this.document.head.appendChild(stylesLink)
  }

  private async loadComponent(): Promise<void> {
    await this.loadScript()
    if (this.destroyed) return

    const list = this.document.createElement(ELIGIBILITY_ELEMENT)
    list.setAttribute('context', this.personaEligibilityListProps)
    this.removeMembershipCanceledListener?.()
    this.removeMembershipCanceledListener = this.renderer.listen(list, 'seMembershipCanceled',
      (event: CustomEvent<{ membershipId: string }>) => {
        if (event.detail?.membershipId) this.membershipCanceled.emit(event.detail)
      }
    )
    this.contents.nativeElement.replaceChildren(list)
  }
}
